import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '../assets/css/RecentWorks.css';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../config';

const RecentWorks = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add responsive state management
  const [screenWidth, setScreenWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [retryCount, setRetryCount] = useState(0);

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setScreenWidth(width);
    setIsMobile(width <= 767);
    setIsTablet(width >= 768 && width <= 992);
  }, []);

  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };

    handleResize();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);

  useEffect(() => {
    setIsLoading(true);
    fetchGalleryItems();
  }, []);

  // Handle image loading errors
  const handleImageError = (index, itemName) => {
    setImageErrors(prev => ({
      ...prev,
      [`${index}-${itemName}`]: true
    }));
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    if (e.currentTarget) {
      e.currentTarget.style.transform = 'scale(0.98) translateY(-2px)';
      e.currentTarget.style.transition = 'transform 0.1s ease';
    }
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    if (e.currentTarget) {
      setTimeout(() => {
        e.currentTarget.style.transform = isMobile ? 'translateY(0)' : 'translateY(-5px)';
        e.currentTarget.style.transition = 'transform 0.3s ease';
      }, 100);
    }
  };

  // Fallback data for when API fails - using actual gallery data
  const fallbackGalleryItems = [
    {
      id: "2",
      name: "WORKSTATION", 
      first_image: {
        image_name: "689498cdc136d_1754568909_0.jpeg",
        image_url: `${UPLOADS_BASE_URL}gallery/689498cdc136d_1754568909_0.jpeg`
      },
      description: "Workstation"
    },
    {
      id: "3",
      name: "RECEPTION DESK",
      first_image: {
        image_name: "68949891db980_1754568849_0.jpeg",
        image_url: `${UPLOADS_BASE_URL}gallery/68949891db980_1754568849_0.jpeg`
      },
      description: "Reception Desk"
    }
    // {
    //   id: "3", 
    //   name: "EXECUTIVE DESK",
    //   first_image: {
    //     image_name: "689447ebea5e2_1754548203_0.jpeg",
    //     image_url: `${UPLOADS_BASE_URL}gallery/689447ebea5e2_1754548203_0.jpeg`
    //   },
    //   description: "Executive Desk"
    // }
  ];

  const fetchGalleryItems = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Fetching specific galleries (IDs: 2, 3, 4) from:', `${API_BASE_URL}gallery.php?include_images=true`);
      
      const response = await fetch(`${API_BASE_URL}gallery.php?include_images=true`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Gallery API response:', data);
      
      // Handle new API response structure - same as Gallery.js
      if (data && data.success && data.galleries && Array.isArray(data.galleries)) {
        console.log('Number of galleries received:', data.galleries.length);
        console.log('All galleries:', data.galleries.map(g => ({ id: g.id, name: g.name, image_count: g.image_count })));
        
        // Filter for specific gallery IDs (2, 3, 4) that have images
        const targetGalleryIds = [2, 3];
        const specificGalleries = [];
        
        // Find galleries with IDs 2, 3, 4 in that order
        targetGalleryIds.forEach(targetId => {
          const foundGallery = data.galleries.find(gallery => 
            parseInt(gallery.id) === targetId && 
            gallery.image_count > 0 && 
            gallery.images && gallery.images.length > 0
          );
          
          if (foundGallery) {
            specificGalleries.push(foundGallery);
            console.log(`✅ Found gallery ID ${targetId}:`, foundGallery.name, `(${foundGallery.image_count} images)`);
            console.log(`   First image:`, foundGallery.images[0]);
          } else {
            console.log(`❌ Gallery ID ${targetId} not found or has no images`);
            // Check if gallery exists but has no images
            const galleryWithoutImages = data.galleries.find(gallery => parseInt(gallery.id) === targetId);
            if (galleryWithoutImages) {
              console.log(`   Gallery ID ${targetId} exists but has ${galleryWithoutImages.image_count} images`);
            }
          }
        });
        
        if (specificGalleries.length > 0) {
          // Process the found galleries (IDs 1, 2, 3) - get first image from each
          const processedItems = specificGalleries.map((gallery, index) => {
            const firstImage = gallery.images && gallery.images.length > 0 ? gallery.images[0] : 
                              gallery.first_image ? gallery.first_image : null;
            
            const processedItem = {
              id: gallery.id,
              name: gallery.name || 'Untitled',
              description: gallery.description || '',
              image_count: gallery.image_count,
              first_image: firstImage
            };
            
            console.log(`RecentWorks - Processing gallery ID ${gallery.id} (position ${index + 1}):`);
            console.log(`  - Name: ${processedItem.name}`);
            console.log(`  - Image count: ${processedItem.image_count}`);
            console.log(`  - First image:`, processedItem.first_image);
            
            return processedItem;
          });
          
          setGalleryItems(processedItems);
          setRetryCount(0);
          setImageErrors({});
          console.log(`✅ Successfully loaded ${processedItems.length} specific galleries (IDs: ${specificGalleries.map(g => g.id).join(', ')})`);
        } else {
          console.log('❌ None of the target galleries (IDs 2, 3, 4) found with images');
          console.log('📋 Available galleries:', data.galleries.map(g => ({ id: g.id, name: g.name, image_count: g.image_count })));
          console.log('🔄 Using fallback data with corrected URLs');
          setGalleryItems(fallbackGalleryItems);
          setImageErrors({});
        }
      } else {
        console.log('❌ Invalid API response structure, using fallback');
        console.log('Response data:', data);
        setGalleryItems(fallbackGalleryItems);
        setImageErrors({});
      }
    } catch (error) {
      console.error('❌ Error fetching gallery items:', error);
      setError(error.message);
      console.log('Using fallback data due to error');
      setGalleryItems(fallbackGalleryItems);
    } finally {
      setIsLoading(false);
    }
  };

  // Retry function for error handling
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setImageErrors({});
    fetchGalleryItems();
  };

  // Add a function to test image URL accessibility
  const testImageUrl = async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
      console.log(`Image URL test for ${url}:`, response.status);
      return true;
    } catch (error) {
      console.error(`Image URL test failed for ${url}:`, error);
      return false;
    }
  };

  // Render gallery item with responsive behavior - Images only
  const renderGalleryItem = (item, index) => {
    const errorKey = `${index}-${item.name}`;
    const hasError = imageErrors[errorKey];
    
    // Get image URL from the API response
    let imageUrl = '';
    if (item.first_image && item.first_image.image_url) {
      imageUrl = item.first_image.image_url;
      
      // Ensure URL is absolute - if API returns relative URL, make it absolute
      if (imageUrl.startsWith('../') || imageUrl.startsWith('./')) {
        // Convert relative URL to absolute using UPLOADS_BASE_URL
        const imageName = imageUrl.split('/').pop();
        imageUrl = `${UPLOADS_BASE_URL}gallery/${imageName}`;
      }
    }
    
    console.log(`Gallery ${item.id} - Image URL: "${imageUrl}"`);
    
    return (
      <Link 
        to="/gallery" 
        key={item.id} 
        className={`recent-work-item item-${index + 1}`}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          ...(isMobile && {
            height: screenWidth <= 480 ? '200px' : '250px',
            marginBottom: '15px'
          }),
          ...(isTablet && {
            height: '280px'
          })
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
          }
        }}
      >
        {imageUrl && !hasError ? (
          <img 
            src={imageUrl} 
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.3s ease'
            }}
            onError={(e) => {
              console.error('Image failed to load:', imageUrl);
              console.error('Setting fallback image');
              e.target.src = 'https://via.placeholder.com/400x300/f0f0f0/666?text=Image+Not+Available';
              handleImageError(index, item.name);
            }}
            onLoad={() => {
              console.log('✅ Image loaded successfully:', imageUrl);
            }}
            onMouseEnter={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isMobile) {
                e.target.style.transform = 'scale(1)';
              }
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '3rem', color: '#ccc' }}>📷</div>
          </div>
        )}
      </Link>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <section className="recent-works">
        <div className="container">
          <div className="loading-container">
            <div style={{ textAlign: 'center', color: '#666' }}>
              Loading Recent Works...
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error && galleryItems.length === 0) {
    return (
      <div 
        className="recent-works-section"
        style={{
          padding: isMobile 
            ? '40px 0' 
            : isTablet 
            ? '60px 0' 
            : '80px 0',
          minHeight: isMobile ? '300px' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div 
          style={{
            textAlign: 'center',
            color: '#666',
            maxWidth: '400px',
            padding: '0 20px'
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: '15px' }}>⚠️</div>
          <h3 style={{ marginBottom: '10px', fontSize: isMobile ? '18px' : '20px' }}>
            Unable to Load Recent Works
          </h3>
          <p style={{ marginBottom: '20px', fontSize: isMobile ? '14px' : '16px' }}>
            {error}
          </p>
          {retryCount < 3 && (
            <button 
              className="retry-btn"
              onClick={handleRetry}
              style={{
                padding: '10px 20px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Retry ({retryCount + 1}/3)
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="recent-works-section"
      style={{
        padding: isMobile 
          ? '40px 0' 
          : isTablet 
          ? '60px 0' 
          : '80px 0'
      }}
    >
      <div 
        className="recent-works-container"
        style={{
          maxWidth: isMobile ? '95%' : isTablet ? '85%' : '80%',
          padding: isMobile ? '0 10px' : '0'
        }}
      >
        <div 
          className="recent-works-titles"
          style={{
            ...(isMobile && {
              position: 'static',
              transform: 'none',
              marginBottom: '30px',
              zIndex: 'auto'
            }),
            ...(isTablet && {
              marginBottom: '40px'
            })
          }}
        >
          <h3 
            className="recent-works-title-small"
            style={{
              fontSize: isMobile 
                ? '1.5rem' 
                : isTablet 
                ? '2rem' 
                : screenWidth >= 1440 
                ? '2.8rem' 
                : '2.5rem',
              marginBottom: isMobile ? '5px' : '0'
            }}
          >
            Discover Our
          </h3>
          <Link 
            to="/gallery" 
            className="recent-works-title-link"
            style={{
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <h2 
              className="recent-works-title-large"
              style={{
                fontSize: isMobile 
                  ? '2.8rem' 
                  : isTablet 
                  ? '4rem' 
                  : screenWidth >= 1440 
                  ? '10rem' 
                  : screenWidth >= 1200 
                  ? '9rem' 
                  : '7rem',
                lineHeight: isMobile ? '1.1' : '1.1',
                marginTop: isMobile ? '0' : '0'
              }}
            >
              Recent works
            </h2>
          </Link>
        </div>
        
        <div 
          className="recent-works-grid"
          style={{
            ...(isMobile && {
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              gridTemplateColumns: 'none',
              gridTemplateRows: 'none'
            }),
            ...(isTablet && {
              gridTemplateRows: 'repeat(15, 35px)',
              gap: '15px'
            }),
            position: 'relative'
          }}
        >
          {galleryItems.length > 0 ? (
            galleryItems.map((item, index) => renderGalleryItem(item, index))
          ) : (
            <div 
              style={{
                gridColumn: '1 / -1',
                gridRow: '1 / -1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                color: '#666',
                textAlign: 'center',
                fontSize: isMobile ? '16px' : '18px'
              }}
            >
              No recent works available
            </div>
          )}
        </div>

        {/* Link to view all works */}
        {!isMobile && galleryItems.length > 0 && (
          <div 
            style={{
              textAlign: 'center',
              marginTop: isTablet ? '40px' : '60px'
            }}
          >
            <Link 
              to="/gallery"
              style={{
                display: 'inline-block',
                padding: isTablet ? '12px 30px' : '15px 40px',
                backgroundColor: 'transparent',
                color: 'var(--primary-color)',
                border: '2px solid var(--primary-color)',
                borderRadius: '30px',
                textDecoration: 'none',
                fontSize: isTablet ? '14px' : '16px',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary-color)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--primary-color)';
              }}
            >
              View All Works
            </Link>
          </div>
        )}
      </div>
      
      {/* Add CSS animation for loading spinner */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RecentWorks; 